import { SUBTITLE_SIZE, TITLE_SIZE } from '../const/chart-const';
import {
  ALMOST_WHITE_COLOR,
  BLACK_COLOR,
  PRIMARY_COLOR,
  PRIMARY_COLOR_SHADES,
  SECONDARY_COLOR,
  SECONDARY_COLOR_SHADES,
} from '../const/color-const';
import { prepareGradientStrokes } from './styles';
import { formatTimestampAsDate } from './dates-utils';
import { localeMap } from './datefns-locale';
import { legendIDs, TOTAL_LABEL } from '../components/common/charts/chartDataConfig';

export const getValues = (valueFields, reports) => {
  return valueFields.map(field => reports.map(report => Number(report[field])));
};

export const getLabels = (labelFields, reports, languageState, oneWeek) => (
  reports.map((report, index) => labelFields.map(field => field === 'Datum' && oneWeek ?
    formatTimestampAsDate(new Date(report[field]), 'ccc', { locale: localeMap[languageState] })
    : (report[field] || `${field} ${index}`))))
;

export const prepareBarChartData = (data) => {
  const floorValues = (number) => (number % 1 === 0) ? Math.floor(number) : number;
  
  data.labels = data.labels.map(([label, value]) => {
    const roundedValue = value && floorValues(value) + ' st';
    return roundedValue ? [label, roundedValue] : [label];
  });
  
  return {
    labels: data.labels,
    datasets: [
      ...data.values.map((dataset, datasetIndex) => {
        const gradient = document.createElement('canvas').getContext('2d').createLinearGradient(0, 0, 0, 500);
        gradient.addColorStop(0, datasetIndex % 2 === 0 ? PRIMARY_COLOR_SHADES[0] : SECONDARY_COLOR_SHADES[0]);
        gradient.addColorStop(0.5, datasetIndex % 2 === 0 ? PRIMARY_COLOR_SHADES[1] : SECONDARY_COLOR_SHADES[1]);
        gradient.addColorStop(1, datasetIndex % 2 === 0 ? PRIMARY_COLOR_SHADES[2] : SECONDARY_COLOR_SHADES[2]);
        return {
          label: data.legends && data.legends[datasetIndex],
          data: dataset,
          backgroundColor: gradient,
          borderColor: datasetIndex % 2 === 0
            ? PRIMARY_COLOR
            : SECONDARY_COLOR,
          borderWidth: 2,
        };
      })],
  };
};

export const prepareLineChartData = (data) => {
  return (canvas) => {
    return {
      labels: data.labels,
      datasets: [
        ...data.values.map((dataset, datasetIndex) => ({
          label: data.legends[datasetIndex],
          data: dataset,
          fill: true,
          backgroundColor: datasetIndex % 2 === 0
            ? prepareGradientStrokes(canvas).primary
            : prepareGradientStrokes(canvas).secondary,
          borderColor: datasetIndex % 2 === 0
            ? PRIMARY_COLOR
            : SECONDARY_COLOR,
          borderWidth: 2,
          borderDash: [],
          borderDashOffset: 0.0,
          pointBackgroundColor: datasetIndex % 2 === 0
            ? PRIMARY_COLOR
            : SECONDARY_COLOR,
          pointBorderColor: 'rgba(255,255,255,0)',
          pointHoverBackgroundColor: PRIMARY_COLOR,
          pointBorderWidth: 20,
          pointHoverRadius: 4,
          pointHoverBorderWidth: 15,
          pointRadius: 4,
        })),
      ],
    };
  };
};

export const prepareOptions = (data, theme, darkState) => ({
  scales: {
    yAxes: [
      {
        gridLines: {
          display: false,
        },
        scaleLabel: {
          display: !!data.money,
          fontColor: darkState ? ALMOST_WHITE_COLOR : BLACK_COLOR,
          fontFamily: '\'Gilroy\', sans-serif',
          labelString: data.money && 'kr',
        },
        ticks: {
          padding: theme.spacing(2),
          fontColor: darkState ? ALMOST_WHITE_COLOR : BLACK_COLOR,
          fontFamily: '\'Gilroy\', sans-serif',
          beginAtZero: data.labelFields[0] !== 'Datum',
        },
      },
    ],
    xAxes: [
      {
        gridLines: {
          display: false,
        },
        ticks: {
          fontColor: darkState ? ALMOST_WHITE_COLOR : BLACK_COLOR,
          fontFamily: '\'Gilroy\', sans-serif',
        },
      },
    ],
  },
  title: {
    display: false,
    fontSize: TITLE_SIZE,
    text: data.title,
  },
  legend: {
    display: true,
    labels: {
      fontSize: SUBTITLE_SIZE,
      fontColor: darkState ? ALMOST_WHITE_COLOR : BLACK_COLOR,
      fontFamily: '\'Gilroy\', sans-serif',
      boxWidth: 30,
    },
  },
  tooltips: {
    callbacks: {
      title: (tooltipItem, data) => {
        const [title, value] = data.labels[tooltipItem[0].index];
        return value === undefined ? title : `${title}: ${value}`;
      },
    },
  },
  layout: {
    padding: {
      top: 10,
      bottom: 20,
    },
  },
  cornerRadius: 6,
  maintainAspectRatio: false,
});

export function getTotalValuesByLegends(legends, data) {
  const totalValues = [];
  for (const legendId of legendIDs) {
    const legend = legends.find(legend => legend.id === legendId);
    if (legend) {
      const totalValue = calculateTotalSum(data, legendId);
      totalValues.push({ id: legend.id, totalValue });
    }
  }
  
  return totalValues;
}

export function calculateTotalSum(data, legendId) {
  const index = data.legends.findIndex(legend => legend.id === legendId);
  if (index === -1) {
    return null;
  }
  
  const allLabelsAreValidDates = data.labels.flat().every(label => {
    return !label.includes('Totalt') && !isNaN(Date.parse(label));
  });
  
  return allLabelsAreValidDates
    ? data.values[index].reduce((acc, value) => acc + (value || 0), 0)
    : data.labels
      .map((label, idx) => (label.includes(TOTAL_LABEL) ? idx : -1))
      .filter(idx => idx !== -1)
      .reduce((acc, idx) => acc + (data.values[index][idx] || 0), 0);
}